import { BoxStaffDetailedEntity, BoxStaffItemEntity } from "@modules/boxStaff/entities/boxStaffEntity"
import {
  BoxStaffActionRequest,
  BoxStaffAddInput,
  BoxStaffDetailedUpadteInput,
} from "@modules/boxStaff/gateway/boxStaffDto"
import { useServicesContext } from "@modules/core/services/services.context"
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"

export const useBoxStaffs = (page?: number, size?: number, nameFilter?: string, fetchArchived?: boolean) => {
  const { boxStaffApi } = useServicesContext()
  const { data, isLoading, isFetching } = useSuspenseQuery({
    queryKey: ["boxStaffs", page, size, nameFilter, fetchArchived],

    queryFn: async () => {
      return await boxStaffApi.getBoxStaffs(page, size, nameFilter, fetchArchived)
    },
  })

  return {
    data: {
      ...data,
      content: data.content.map((boxStaff) => BoxStaffItemEntity.fromItemDto(boxStaff)) || [],
    },
    isFetching,
    isLoading,
  }
}

export const useBoxStaff = (id: string) => {
  const { boxStaffApi } = useServicesContext()
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ["boxStaffs", id],
    queryFn: async () => {
      return await boxStaffApi.getBoxStaff(id)
    },
  })

  // Update role
  const { mutateAsync: updateBoxStaff, isPending: isUpdateBoxStaff } = useMutation({
    mutationFn: async (input: BoxStaffDetailedUpadteInput) => {
      return await boxStaffApi.updateBoxStaff(id, input)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["boxStaffs"],
      })
    },
  })

  // Delete box staff
  const { mutateAsync: deleteBoxStaff, isPending: isDeleteBoxStaff } = useMutation({
    mutationFn: async () => {
      return await boxStaffApi.deleteBoxStaff(id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["boxStaffs"],
      })
    },
  })

  const isPending = isUpdateBoxStaff || isDeleteBoxStaff

  return {
    data: data ? BoxStaffDetailedEntity.fromDetailedDto(data) : undefined,
    isLoading,
    deleteBoxStaff,
    updateBoxStaff,
    isPending,
  }
}

export const useAddBoxStaff = () => {
  const { boxStaffApi } = useServicesContext()
  const queryClient = useQueryClient()

  // Add box staff
  const { mutateAsync: addBoxStaff, isPending: isAddBoxStaff } = useMutation({
    mutationFn: async (input: BoxStaffAddInput) => {
      return await boxStaffApi.addBoxStaff(input)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["boxStaffs"],
      })
    },
  })

  return {
    addBoxStaff,
    isPending: isAddBoxStaff,
  }
}

export const useBoxStaffInvitation = () => {
  const { boxStaffApi } = useServicesContext()
  const queryClient = useQueryClient()

  // Accept box staff invitation
  const { mutateAsync: responseBoxStaffInvitation, isPending: isAcceptBoxStaffInvitation } = useMutation({
    mutationFn: async ({ boxId, action }: { boxId: string; action: BoxStaffActionRequest }) => {
      return await boxStaffApi.responseBoxStaffInvitation(boxId, action)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["boxStaffs"],
      })
    },
  })

  const isPending = isAcceptBoxStaffInvitation

  return {
    responseBoxStaffInvitation,
    isPending,
  }
}
